import { OVERLOADED } from "./problem.ts";

/**
 * `contracts/client-api.md` § Retry and backoff, FR-015-141: three attempts for a `GET`, two
 * retries for a mutation with the same `Idempotency-Key`, the retried status set, exponential
 * backoff with full jitter in `[0, min(8000, 250 * 2^n))`, `Retry-After` exactly for a 429 and a
 * 503 `overloaded`, no retry for the answer statuses, and cancellation through the caller's
 * `AbortSignal`.
 */
export const RETRIED_STATUSES: ReadonlySet<number> = new Set([408, 429, 500, 502, 503, 504]);

/** The statuses that are answers, not failures, and are never retried. */
export const ANSWER_STATUSES: ReadonlySet<number> = new Set([400, 401, 403, 404, 409, 412, 413, 415, 422]);

export const MAX_ATTEMPTS_READ = 3;
/** Two retries after the first attempt. */
export const MAX_ATTEMPTS_MUTATION = 3;

/** `download` is a read whose answer is a file (001 § Export of a task list): retried as a read, with the upload's longer timeout, since the server builds the whole file before its first byte. */
export type RequestKind = "read" | "mutation" | "upload" | "download";

export function maxAttempts(kind: RequestKind): number {
  return kind === "read" || kind === "download" ? MAX_ATTEMPTS_READ : MAX_ATTEMPTS_MUTATION;
}

/** Whether an attempt that failed with `status` (0 for a network failure or timeout) may be followed by another. */
export function shouldRetry(kind: RequestKind, status: number, attempt: number): boolean {
  if (attempt >= maxAttempts(kind)) return false;
  if (status === 0) return true;
  if (ANSWER_STATUSES.has(status)) return false;
  return RETRIED_STATUSES.has(status);
}

/** Attempt `n` (1-based) waits a random duration in `[0, min(8000, 250 * 2^n))` ms. */
export function backoffDelay(attempt: number, random: () => number = Math.random): number {
  const ceiling = Math.min(8000, 250 * 2 ** attempt);
  return Math.floor(random() * ceiling);
}

/**
 * Whether a refusal says when to come back: a 429, and a 503 `overloaded`, which 001 answers when
 * the instance had no database connection free for the request and which names in `Retry-After`
 * when one is likely to be (2026-09-29). A 503 `unavailable` is an instance not serving, whose
 * return nobody can predict, so it backs off like any failure.
 */
export function waitsRetryAfter(status: number, type: string): boolean {
  return status === 429 || (status === 503 && type === OVERLOADED);
}

/** A refusal `waitsRetryAfter` names waits `Retry-After` exactly rather than backing off; anything else backs off. */
export function delayBeforeRetry(status: number, type: string, attempt: number, retryAfterMs: number | null, random?: () => number): number {
  if (waitsRetryAfter(status, type) && retryAfterMs !== null) return retryAfterMs;
  return backoffDelay(attempt, random);
}

/** Sleep that rejects with the signal's reason when the caller cancels. */
export function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError(signal));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      reject(abortError(signal));
    }
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export function abortError(signal?: AbortSignal): Error {
  const reason: unknown = signal?.reason;
  if (reason instanceof Error) return reason;
  return new DOMException("The request was cancelled", "AbortError");
}

export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}
